import React from 'react';
import { Box, Pencil, Trash2 } from 'lucide-react';
import {
  environmentLabel,
  inferenceModeLabel,
  modalityLabel,
  providerLabel,
  statusLabel,
  taskLabel,
} from './aiModelConstants';
import { AiRegisteredModel } from './aiModelTypes';

function statusClasses(status: AiRegisteredModel['status']): string {
  switch (status) {
    case 'registered':
      return 'text-green-400 bg-green-400/10 border-green-400/20';
    case 'deprecated':
      return 'text-gray-400 bg-gray-400/10 border-gray-400/20';
    default:
      return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20';
  }
}

function environmentClasses(environment: AiRegisteredModel['environment']): string {
  if (environment === 'production') return 'bg-indigo-500/15 text-indigo-300';
  if (environment === 'staging') return 'bg-sky-500/15 text-sky-300';
  return 'bg-neutral-500/15 text-neutral-300';
}

interface AiModelCardProps {
  model: AiRegisteredModel;
  isLight: boolean;
  onEdit: (model: AiRegisteredModel) => void;
  onDelete: (model: AiRegisteredModel) => void;
}

const AiModelCard: React.FC<AiModelCardProps> = ({ model, isLight, onEdit, onDelete }) => {
  const iconBtn = isLight
    ? 'text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100'
    : 'text-gray-400 hover:text-white hover:bg-neutral-800';

  return (
    <div
      className={`rounded-lg p-5 border flex flex-col transition-all group ${
        isLight
          ? 'bg-white border-neutral-200 hover:border-indigo-400/40 shadow-sm'
          : 'bg-neutral-900 border-neutral-800 hover:border-indigo-500/30'
      }`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3 min-w-0">
          <div
            className={`p-2.5 rounded-lg shrink-0 ${
              isLight ? 'bg-neutral-100 border border-neutral-200' : 'bg-neutral-800 border border-neutral-700'
            }`}
          >
            <Box size={20} className={isLight ? 'text-neutral-700' : 'text-white'} />
          </div>
          <div className="min-w-0">
            <h3 className={`font-semibold text-base leading-tight truncate ${isLight ? 'text-neutral-900' : 'text-white'}`}>
              {model.name}
            </h3>
            <p className={`text-xs mt-0.5 font-mono ${isLight ? 'text-neutral-500' : 'text-gray-400'}`}>
              {model.code} · {model.version}
            </p>
          </div>
        </div>
        <span className={`shrink-0 px-2 py-1 rounded-md text-xs font-medium border ${statusClasses(model.status)}`}>
          {statusLabel(model.status)}
        </span>
      </div>

      <p className={`text-xs leading-relaxed line-clamp-2 mb-3 ${isLight ? 'text-neutral-600' : 'text-gray-300'}`}>
        {model.description || 'No description provided.'}
      </p>

      <div className="flex flex-wrap gap-1 mb-3">
        <span
          className={`px-2 py-0.5 rounded-full text-xs ${
            isLight ? 'bg-neutral-100 text-neutral-600' : environmentClasses(model.environment)
          }`}
        >
          {environmentLabel(model.environment)}
        </span>
        <span className={`px-2 py-0.5 rounded-full text-xs ${isLight ? 'bg-neutral-100 text-neutral-600' : 'bg-neutral-800 text-gray-300'}`}>
          {providerLabel(model.provider)}
        </span>
        <span className={`px-2 py-0.5 rounded-full text-xs ${isLight ? 'bg-neutral-100 text-neutral-600' : 'bg-neutral-800 text-gray-300'}`}>
          {taskLabel(model.task)}
        </span>
        <span className={`px-2 py-0.5 rounded-full text-xs ${isLight ? 'bg-neutral-100 text-neutral-600' : 'bg-neutral-800 text-gray-300'}`}>
          {modalityLabel(model.modality)}
        </span>
      </div>

      <div className={`text-xs space-y-1 mb-3 ${isLight ? 'text-neutral-500' : 'text-gray-400'}`}>
        <p>
          Inference{' '}
          <span className={isLight ? 'text-neutral-700' : 'text-gray-300'}>{inferenceModeLabel(model.inferenceMode)}</span>
        </p>
        <p className="truncate font-mono">
          {model.endpointId}
        </p>
        {model.lastTest ? (
          <p>
            Last test{' '}
            <span className={model.lastTest.success ? 'text-emerald-400' : 'text-amber-400'}>
              {model.lastTest.success ? 'passed' : 'incomplete'} · {model.lastTest.latencyMs} ms
            </span>
          </p>
        ) : (
          <p>Not tested</p>
        )}
      </div>

      <div className={`mt-auto flex items-center space-x-2 pt-3 border-t ${isLight ? 'border-neutral-200' : 'border-neutral-800'}`}>
        <button
          type="button"
          onClick={() => onEdit(model)}
          title="Edit"
          className={`p-1.5 rounded-md transition-colors ${iconBtn}`}
        >
          <Pencil size={14} />
        </button>
        <button
          type="button"
          onClick={() => onDelete(model)}
          title="Delete"
          className={`p-1.5 rounded-md transition-colors ${
            isLight ? 'text-neutral-400 hover:text-red-500 hover:bg-red-50' : 'text-gray-400 hover:text-red-400 hover:bg-neutral-800'
          }`}
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
};

export default AiModelCard;
